import { Injectable } from '@angular/core';
import { ApiResponse } from '../../Interfaces/apiResponse.interface';
import {
  ENDPOINTS_BUYER,
  ENDPOINTS_CONTRACTOR,
} from '../../constants/ENDPOINTS';
import { Observable, of } from 'rxjs';
import { HttpBaseService } from '../httpBase/httpBase.service';
import {
  QuotationRFPComparisonItem,
  QuotationRFPDetails,
  QuotationRFPListInsights,
  QuotationRFPListResponse,
  RelatedQuotationsRFPResponse,
} from '../../models/quotation-rfp';
import { HttpResponse } from '@angular/common/http';
import { RFPGroup } from '../../models/rfp';

@Injectable({
  providedIn: 'root',
})
export class QuotationRFPService extends HttpBaseService {
  createQuotation(
    rfpId: number,
    data: { items: { id: number; price: number }[] }
  ) {
    const url = ENDPOINTS_CONTRACTOR.QUOTATIONS.POST_CREATE_QUOTATION.replace(
      '{rfp}',
      rfpId.toString()
    );
    return this.post(url, data);
  }

  cancelQuotation(
    rfpId: number
  ): Observable<HttpResponse<{ message?: string }>> {
    const url = `${ENDPOINTS_CONTRACTOR.QUOTATIONS.POST_CANCEL_QUOTATION.replace('{rfp}', rfpId.toString())}`;
    return this.post(url, {}, { observe: 'response' });
  }

  submitQuotation(
    rfpId: number,
    quotationId: number,
    data: { attachments: string[] }
  ) {
    const url = `${ENDPOINTS_CONTRACTOR.QUOTATIONS.POST_SUBMIT_QUOTATION.replace('{rfp}', rfpId.toString()).replace('{quotations}', quotationId.toString())}`;
    return this.post(url, data);
  }

  getContractorQuotationsRFPInsights(): Observable<
    ApiResponse<QuotationRFPListInsights>
  > {
    const url = ENDPOINTS_CONTRACTOR.QUOTATIONS.GET_QUOTATION_LIST_INSIGHTS;
    return this.get(url);
  }

  getContractorQuotationsRFPList(
    page = 1,
    items_per_page = 25,
    state?: 'active' | 'inActive'
  ): Observable<QuotationRFPListResponse> {
    const url = `${ENDPOINTS_CONTRACTOR.QUOTATIONS.GET_QUOTATION_LIST}?page=${page}&items_per_page=${items_per_page}${state ? '&state=' + state : ''}`;
    return this.get(url);
  }

  getContractorQuotationDetails(
    rfpId: number,
    quotationId: number
  ): Observable<ApiResponse<QuotationRFPDetails>> {
    const url = `${ENDPOINTS_CONTRACTOR.QUOTATIONS.GET_QUOTATION_DETAILS.replace('{rfp}', (rfpId ?? '').toString()).replace('{quotation}', (quotationId ?? '').toString())}`;
    return this.get(url);
  }

  /* Buyer */
  getBuyerQuotationsRFPInsights(): Observable<
    ApiResponse<QuotationRFPListInsights>
  > {
    const url = ENDPOINTS_BUYER.QUOTATIONS_RFP.GET_QUOTATION_LIST_INSIGHTS;
    return this.get(url);
  }

  getBuyerQuotationsRFPList(
    page = 1,
    items_per_page = 25,
    state?: 'active' | 'inActive'
  ): Observable<QuotationRFPListResponse> {
    const url = `${ENDPOINTS_BUYER.QUOTATIONS_RFP.GET_QUOTATION_LIST}?page=${page}&items_per_page=${items_per_page}${state ? '&state=' + state : ''}`;
    return this.get(url);
  }

  getBuyerQuotationDetails(
    rfpId: number,
    quotationId: number
  ): Observable<ApiResponse<QuotationRFPDetails>> {
    const url = `${ENDPOINTS_BUYER.QUOTATIONS_RFP.GET_QUOTATION_DETAILS.replace('{rfp}', (rfpId ?? '').toString()).replace('{quotation}', (quotationId ?? '').toString())}`;
    return this.get(url);
  }

  getBuyerQuotationDetailsGroups(
    rfpId: number,
    quotationId: number
  ): Observable<ApiResponse<RFPGroup[]>> {
    const url = `${ENDPOINTS_BUYER.QUOTATIONS_RFP.GET_QUOTATION_DETAILS_GROUPS.replace('{rfp}', (rfpId ?? '').toString()).replace('{quotation}', (quotationId ?? '').toString())}`;
    return this.get(url);
  }

  declineQuotation(
    quotationId: number
  ): Observable<HttpResponse<{ message?: string }>> {
    const url = `${ENDPOINTS_BUYER.QUOTATIONS_RFP.POST_DECLINE_QUOTATION.replace('{quotation}', (quotationId ?? '').toString())}`;
    return this.post(url, {}, { observe: 'response' });
  }

  awardQuotation(
    quotationId: number,
    rfpId: number
  ): Observable<{ data: { purchase_order_id: number; message?: string } }> {
    const url = `${ENDPOINTS_BUYER.QUOTATIONS_RFP.POST_AWARD_QUOTATION.replace('{quotation}', (quotationId ?? '').toString()).replace('{rfp}', (rfpId ?? '').toString())}`;
    return this.post(url, {});
  }

  getRFPRelatedQuotations(
    rfpId: number,
    state?: 'active' | 'inActive'
  ): Observable<RelatedQuotationsRFPResponse> {
    const url = `${ENDPOINTS_BUYER.QUOTATIONS_RFP.GET_RFQ_RELATED_QUOTATIONS.replace('{rfp}', rfpId.toString())}?${state ? 'state=' + state : ''}`;
    return this.get(url);
  }

  getBuyerQuotationComparison(
    rfpId: number,
    quotation_ids: number[]
  ): Observable<ApiResponse<QuotationRFPComparisonItem[]>> {
    const url = `${ENDPOINTS_BUYER.QUOTATIONS_RFP.GET_QUOTATION_COMPARISON.replace('{rfp}', rfpId.toString())}`;
    return this.post(url, { quotation_ids });
  }
}
